// src/features/admin/services/SystemPreferencesAPI.ts

import type { AppPreferences } from '@/types/appPreferences';
import type { NotificationPreferences } from '@/features/edgemail-client/store/settingsStore';

const API_BASE_URL = '/api/v1/admin/system-preferences';

/**
 * Get authentication token from Keycloak or storage
 */
function getAuthToken(): string | null {
  // Try Keycloak instance first (primary method)
  const keycloak = (window as any).keycloak;
  if (keycloak?.token) {
    return keycloak.token;
  }

  // Fallback to localStorage/sessionStorage
  return (
    localStorage.getItem('edgelex_token') ||
    sessionStorage.getItem('edgelex_token') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token') ||
    localStorage.getItem('edgelex_access_token')
  );
}

/**
 * System Preferences Interface
 */
export interface SystemPreferences {
  id?: string;
  firm_id?: string;
  
  // Regional & Localization
  timezone: string;
  date_format: string;
  time_format: string;
  currency: string;
  language: string;
  legal_jurisdictions: string[];
  
  // Typography & Display
  system_font: string;
  document_font: string;
  font_size_scale: string;
  contrast_mode: string;
  measurement_unit: string;

  // UI Appearance Defaults
  ui_theme_mode?: string;
  ui_accent_color?: string;
  ui_accent_custom_hex?: string;
  ui_density?: string;
  ui_neutral_base?: string;
  /** Firm default Lex theme: a gallery theme id, 'custom', or null (legacy individual settings). */
  ui_lex_theme?: string | null;
  /** Firm default EdgeMail wallpaper: a catalogue id, 'firm' (uploaded image), 'none', or null (product default). */
  ui_mail_wallpaper?: string | null;
  ui_app_defaults?: AppPreferences;
  ui_notification_defaults?: Partial<NotificationPreferences>;
  
  // Document Defaults
  default_page_size: string;
  default_page_orientation: string;
  default_margin_preset: string;
  default_line_spacing: string;
  default_line_numbering: boolean;
  default_show_page_numbers: boolean;
  
  // Security & Compliance
  session_timeout_minutes: number;
  require_2fa: boolean;
  audit_logging_enabled: boolean;
  confidentiality_mode_enabled: boolean;
  document_watermarking_enabled: boolean;
  
  // Calendar & Scheduling
  business_hours_start: string;
  business_hours_end: string;
  default_meeting_duration_minutes: number;
  default_reminder_minutes: number;
  weekend_saturday: boolean;
  weekend_sunday: boolean;
  holiday_calendars: string[];
  
  // Metadata
  created_at?: string;
  updated_at?: string;
}

/**
 * Get system preferences for the authenticated firm
 */
export const getSystemPreferences = async (): Promise<SystemPreferences> => {
  const token = getAuthToken();
  
  const response = await fetch(API_BASE_URL, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch system preferences: ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
};

/**
 * Update system preferences
 */
export const updateSystemPreferences = async (
  preferences: Partial<SystemPreferences>
): Promise<SystemPreferences> => {
  const token = getAuthToken();
  
  const response = await fetch(API_BASE_URL, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(preferences),
  });

  if (!response.ok) {
    throw new Error(`Failed to update system preferences: ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
};

/**
 * Reset system preferences to defaults
 */
export const resetSystemPreferences = async (): Promise<SystemPreferences> => {
  const token = getAuthToken();
  
  const response = await fetch(`${API_BASE_URL}/reset`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to reset system preferences: ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
};
